import * as React from "react";
import ThreadLayoutItem from "../threadLayoutItem";
import UserProfileBox from "common/components/userProfileBox";
import { Username } from "../threadLayoutItem/styledComponents";
import {
  COMMENT_PROFILE_SIZE,
  REPLY_COMMENT_PROFILE_SIZE,
} from "app/main/constants";
import { ThreadTopLabel, ThreadFooterItem } from "../styledComponents";
import Icon from "common/icons";
import PACKS from "common/icons/packs";
import moment from "moment";
import { FormattedMessage } from "react-intl";
import { useEventCallback } from "@vingle/wtf-sdk/helpers/hook";
import LikeControl from "common/components/likeControl";
import ThreadContentRenderer from "common/components/threadContentRenderer";
import { VingleURL } from "@vingle/wtf-sdk/url";

const ThreadFooterLabelItem = ThreadFooterItem.withComponent("label");

interface IProps {
  thread: Vingle.IThread;
  onLike: (thread: Vingle.IThread) => void;
  isPreview?: boolean;
  parentId?: number | string;
  isReply?: boolean;
  onReplyWrite?: (thread: Vingle.IThread) => void;
  handleHighlightUser?: (comment: Vingle.IThread) => boolean;
  disableEngage?: boolean;
}

const ThreadItem = React.memo(
  ({
    thread,
    parentId,
    onLike,
    handleHighlightUser,
    onReplyWrite,
    isReply = false,
    isPreview = false,
    disableEngage = false,
  }: IProps) => {
    const handleReplyWrite = useEventCallback(() => {
      if (onReplyWrite) {
        onReplyWrite(thread);
      }
    }, [thread]);
    const handleLike = useEventCallback(() => {
      if (onLike) {
        onLike(thread);
      }
    }, [onLike, thread]);
    const profileSize = React.useMemo(
      () => (isReply ? REPLY_COMMENT_PROFILE_SIZE : COMMENT_PROFILE_SIZE),
      [isReply],
    );
    const userURL = new VingleURL.UserShow({
      username: thread.user.username,
    });

    if (thread.hidden) {
      return null;
    }

    return (
      <ThreadLayoutItem
        isHidden={thread.hidden}
        minimum={isReply}
        left={
          <UserProfileBox
            width={profileSize}
            height={profileSize}
            user={thread.user}
          />
        }
        header={
          <span>
            <Username
              to={userURL.toString()}
              highlight={
                handleHighlightUser ? handleHighlightUser(thread) : false
              }
            >
              {thread.user.username}
            </Username>
            {thread.isTop ? (
              <ThreadTopLabel>
                <Icon icon={PACKS.COMMENT.TOP} />
              </ThreadTopLabel>
            ) : null}
          </span>
        }
        body={
          <ThreadContentRenderer
            thread={thread}
            isPreview={disableEngage || isPreview}
          />
        }
        footerLeft={
          !disableEngage ? (
            <React.Fragment>
              <ThreadFooterLabelItem active={false}>
                {moment(thread.createdAt).fromNow()}
              </ThreadFooterLabelItem>
              {thread.isEdited ? (
                <ThreadFooterLabelItem active={false}>
                  <FormattedMessage id="is_edited" />
                </ThreadFooterLabelItem>
              ) : null}
              {onReplyWrite && (
                <ThreadFooterLabelItem
                  htmlFor={
                    parentId ? `replyCommentInput${parentId}` : undefined
                  }
                  active={true}
                  onClick={handleReplyWrite}
                >
                  <FormattedMessage id="add_reply" />
                </ThreadFooterLabelItem>
              )}
            </React.Fragment>
          ) : null
        }
        footerRight={
          !disableEngage && thread.liked !== undefined ? (
            <LikeControl
              isLiked={Boolean(thread.liked)}
              likesCount={thread.likesCount || 0}
              onLikeClick={handleLike}
            />
          ) : null
        }
      />
    );
  },
);

export default ThreadItem;
